import {
  AddFileParams,
  AddFolderParams,
  FileFolderPath,
  Folder,
} from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useConversation } from 'chat/conversations/queries/detail'
import { useUpdateConversationLastViewed } from 'chat/conversations/timestamps'
import { isRepository } from 'chat/conversations/utils'
import { useFileFolderActions } from 'file_folder_repository/context'
import { useChatGroupFileFolders } from 'file_folder_repository/queries/list'
import { useChatGroupMedia } from 'file_folder_repository/queries/media'
import {
  filterResourceList,
  getFileFolderList,
  sortResourceList,
} from 'file_folder_repository/util'
import { AddFileFolderModalType } from 'file_folder_repository/views/add_file_folder_modal'
import { ChatGroupFoldersScreenProps } from 'home/stack/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  IconButton,
  showHeaderButtonList,
} from 'shared/header/header_button_list'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'
import { ChatGroupFoldersView } from './views'

export function ChatGroupFoldersScreen({
  navigation,
  route,
}: ChatGroupFoldersScreenProps) {
  const { conversationId, path } = route.params
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0)
  const [currentFolderId, setCurrentFolderId] = useState<string>()

  const { theme } = useTheme()
  const { handleNavigation } = useRoutingContext()
  const userQuery = useUser()
  const {
    onLongPressResource,
    setConversationId,
    selectedAction,
    onPerformSelectedAction: onPerformAction,
    onCancelSelectedAction,
    openSortOrderBottomSheet,
    sortOption,
    onPressFile,
    setShouldNavigateToFoldersScreen,
    viewMode,
    onAddFile,
    onAddFolder,
  } = useFileFolderActions()

  const conversationQuery = useConversation(conversationId)
  const chatGroupMediaQuery = useChatGroupMedia(conversationId)
  const fileFolderRepositoryQuery = useChatGroupFileFolders(
    conversationId,
    currentFolderId
  )

  const { mutateAsync: conversationUpdateLastViewed } =
    useUpdateConversationLastViewed(conversationId)

  const isUpdatingLastViewedRef = useRef(false)

  const updateLastViewed = useCallback(async () => {
    if (
      isUpdatingLastViewedRef.current ||
      !conversationQuery.data ||
      !isRepository(conversationQuery.data)
    ) {
      return
    }
    isUpdatingLastViewedRef.current = true
    try {
      await conversationUpdateLastViewed()
    } catch (e) {
      console.log(e)
    } finally {
      isUpdatingLastViewedRef.current = true
    }
  }, [conversationQuery.data, conversationUpdateLastViewed])

  const sortedFileFolderList = useMemo(
    () =>
      filterResourceList(
        sortResourceList(
          [
            ...getFileFolderList(fileFolderRepositoryQuery.data),
            ...(chatGroupMediaQuery.data && currentFolderId === undefined
              ? chatGroupMediaQuery.data
              : []),
          ],
          sortOption
        ),
        selectedFilterIndex
      ),
    [
      chatGroupMediaQuery.data,
      currentFolderId,
      fileFolderRepositoryQuery.data,
      selectedFilterIndex,
      sortOption,
    ]
  )

  const HomeBreadCrumb: FileFolderPath = useMemo(
    () => ({ id: undefined, name: t('files') }),
    []
  )

  const [breadcrumbs, setBreadcrumbs] = useState<FileFolderPath[]>([
    HomeBreadCrumb,
  ])

  const onPressFolder = useCallback(
    (folder: Folder) => {
      setCurrentFolderId(folder.id)
      setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }])
      setSelectedFilterIndex(0)
    },
    [setCurrentFolderId]
  )

  const onPressBreadcrumb = useCallback(
    (index: number) => {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1)
      setCurrentFolderId(newBreadcrumbs[newBreadcrumbs.length - 1]?.id)
      setBreadcrumbs(newBreadcrumbs)
      setSelectedFilterIndex(0)
    },
    [breadcrumbs, setCurrentFolderId]
  )

  const onPressSearchBarButton = useCallback(() => {
    handleNavigation({
      routeName: 'file_folder_search',
      routeParams: { conversationId },
    })
  }, [conversationId, handleNavigation])

  const onPerformSelectedAction = useCallback(async () => {
    await onPerformAction(currentFolderId)
    setSelectedFilterIndex(0)
  }, [currentFolderId, onPerformAction])

  const onUploadFileFolder = useCallback(
    async (
      fields: AddFileParams & AddFolderParams,
      type: AddFileFolderModalType
    ) => {
      const queryParams: AddFileParams & AddFolderParams = {
        ...fields,
        parentFolderId: currentFolderId,
      }

      if (type === 'file' || type === 'url') {
        await onAddFile(queryParams)
      } else {
        await onAddFolder(queryParams)
      }
    },
    [currentFolderId, onAddFile, onAddFolder]
  )
  useEffect(() => {
    setConversationId(conversationId)
  }, [conversationId, setConversationId])

  useEffect(() => {
    setShouldNavigateToFoldersScreen(false)
    return () => {
      setCurrentFolderId(undefined)
      setShouldNavigateToFoldersScreen(true)
    }
  }, [setCurrentFolderId, setShouldNavigateToFoldersScreen])

  useEffect(() => {
    if (path) {
      setBreadcrumbs([HomeBreadCrumb, ...path])
      if (path.length > 0) {
        setCurrentFolderId(path[path.length - 1].id)
      }
    }
  }, [HomeBreadCrumb, path])

  useEffect(() => {
    updateLastViewed().catch(e => console.log(e))
  }, [updateLastViewed])

  useEffect(() => {
    if (!conversationQuery.data || !sortedFileFolderList.length) {
      return
    }

    const latestFileTimestamp =
      conversationQuery.data.status?.recentMessage?.createdAt ?? 0
    const conversationLastViewedTimestamp =
      conversationQuery.data.status?.lastViewedTimestamp ?? 0

    if (latestFileTimestamp > conversationLastViewedTimestamp) {
      updateLastViewed().catch(e => console.log(e))
    }
  }, [conversationQuery.data, sortedFileFolderList.length, updateLastViewed])

  useLayoutEffect(() => {
    const headerButtons: IconButton[] = [
      {
        onPress: () => openSortOrderBottomSheet(),
        name: 'sort',
        type: 'material-icons',
      },
    ]

    if (isRepository(conversationQuery?.data)) {
      headerButtons.push({
        onPress: () => {
          handleNavigation({
            routeName: 'conversation_details',
            routeParams: { conversationId },
          })
        },
        name: 'information-circle-outline',
        type: 'ionicon',
      })
    }

    showHeaderButtonList({
      navigation,
      buttons: headerButtons,
      theme,
    })
  }, [
    conversationId,
    conversationQuery.data?.name,
    conversationQuery.data?.type,
    handleNavigation,
    navigation,
    openSortOrderBottomSheet,
    theme,
  ])

  useLayoutEffect(() => {
    if (isRepository(conversationQuery?.data)) {
      showHeaderTitle(navigation, conversationQuery.data?.name ?? '')
    }
  }, [conversationQuery.data?.name, conversationQuery.data?.type, navigation])

  return (
    <ChatGroupFoldersView
      conversation={conversationQuery.data}
      chatGroupFileFolderList={sortedFileFolderList}
      breadcrumbs={breadcrumbs}
      onLongPressResource={onLongPressResource}
      onPressBreadcrumb={onPressBreadcrumb}
      onPressFile={onPressFile}
      onPressFolder={onPressFolder}
      onPerformSelectedAction={onPerformSelectedAction}
      onCancelSelectedAction={onCancelSelectedAction}
      loading={fileFolderRepositoryQuery.isLoading}
      selectedFilterIndex={selectedFilterIndex}
      setSelectedFilterIndex={setSelectedFilterIndex}
      onPressSearchBarButton={onPressSearchBarButton}
      sortOption={sortOption}
      selectedAction={selectedAction}
      viewMode={viewMode}
      user={userQuery.data}
      onUploadFileFolder={onUploadFileFolder}
    />
  )
}
